import { isFile } from '@databases/utils/utils';

import { RawImportFile } from '../types';
import { importMimeTypes } from '../util';

/**
 * Takes a list of pasted files and loads the one that is the most suitable for a database import. Detects the file type
 * but reads the file into a string, without any parsing. If there is a problem with the picked file (for example is has
 * an unsupported MIME type), an error is thrown.
 */
export async function readImportFile(files: File[] | DataTransferItemList): Promise<
  | { success: true; file: RawImportFile }
  | {
      success: false;
      error: { tag: 'UNSUPPORTED_MIME_TYPE'; fileType: string };
    }
  | {
      success: false;
      error: { tag: 'PASTED_FILE_CANNOT_BE_READ' | 'UNEXPECTED_DATA_TYPE' };
    }
> {
  if (Array.isArray(files) && isFile(files[0])) {
    return {
      success: true,
      file: {
        name: files[0].name,
        mimeType: files[0].type,
        rawContent: await files[0].text(),
        size: files[0].size,
        type: importMimeTypes[files[0].type],
      },
    };
  } else if (files instanceof DataTransferItemList) {
    const items = Array.from(files);

    const stringItems = items.filter((item) => item.kind === 'string');
    if (stringItems.length > 0) {
      for (const [mimeType, type] of Object.entries(importMimeTypes)) {
        const item = stringItems.find((item) => item.type === mimeType);
        if (item) {
          const rawContent = await new Promise<string>((resolve) => {
            item.getAsString(resolve);
          });
          if (!rawContent) {
            return {
              success: false,
              error: {
                tag: 'PASTED_FILE_CANNOT_BE_READ',
              },
            };
          }
          return {
            success: true,
            file: {
              name: `Pasted content (${type})`, // TODO: NCTD-2653
              mimeType,
              rawContent,
              size: 0,
              type,
            },
          };
        }
      }

      return {
        success: false,
        error: {
          tag: 'UNSUPPORTED_MIME_TYPE',
          fileType: stringItems.map((item) => item.type).join(', '),
        },
      };
    }

    const fileItem = items.find((item) => item.kind === 'file');
    if (fileItem) {
      if (!importMimeTypes[fileItem.type]) {
        return {
          success: false,
          error: {
            tag: 'UNSUPPORTED_MIME_TYPE',
            fileType: fileItem.type,
          },
        };
      }

      const file = fileItem.getAsFile();
      if (!file) {
        return {
          success: false,
          error: {
            tag: 'PASTED_FILE_CANNOT_BE_READ',
          },
        };
      }
      return {
        success: true,
        file: {
          name: file.name,
          type: importMimeTypes[fileItem.type],
          mimeType: file.type,
          rawContent: await file.text(),
          size: 0,
        },
      };
    }
  }

  return { success: false, error: { tag: 'UNEXPECTED_DATA_TYPE' } };
}
